import React, { useEffect } from "react";
import parse from "html-react-parser";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface OfferLetterViewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  offerLetterContent: string;
  jobTitle: string;
  status?: number;
  candidateSignatureMode?: string;
  candidateSignatureUrl?: string;
  candidateSignatureName?: string;
  declineReason?: string;
}

export default function OfferLetterViewPanel({
  isOpen,
  onClose,
  offerLetterContent,
  jobTitle,
  status,
  candidateSignatureMode,
  candidateSignatureUrl,
  candidateSignatureName,
  declineReason,
}: OfferLetterViewPanelProps) {
  const { t } = useTranslation("job-details");

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Format text to handle line breaks
  const formatOfferLetterContent = (content: string) => {
    if (!content) return "";
    return parse(content);
  };

  return (
    <div className="offer-preview-panel-overlay" onClick={onClose}>
      <div className="offer-preview-panel" onClick={(e) => e.stopPropagation()}>
        <div className="offer-preview-header">
          <h3>{t("jobDetails.finalDecision.offerStatus.viewOffer")}</h3>
          <p>
            {t("jobDetails.finalDecision.previewPanel.for")} {jobTitle}
          </p>
          <button className="close-panel" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="offer-preview-content">
          {/* Status Banner */}
          {status === 7 && (
            <div
              className="offer-status-banner"
              style={{
                backgroundColor: "#dcfce7",
                color: "#15803d",
                padding: "12px 20px",
                borderLeft: "4px solid #22c55e",
                marginBottom: "16px",
              }}
            >
              <strong>{t("jobDetails.finalDecision.offerStatus.offerAccepted")}</strong>
            </div>
          )}
          {status === 8 && (
            <>
              <div
                className="offer-status-banner"
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  padding: "12px 20px",
                  borderLeft: "4px solid #ef4444",
                  marginBottom: declineReason ? "0" : "16px",
                  borderRadius: declineReason ? "4px 4px 0 0" : undefined,
                }}
              >
                <strong>{t("jobDetails.finalDecision.offerStatus.offerRejected")}</strong>
              </div>
              {declineReason && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    color: "#7f1d1d",
                    padding: "10px 20px",
                    borderLeft: "4px solid #ef4444",
                    marginBottom: "16px",
                    fontSize: "14px",
                    borderRadius: "0 0 4px 4px",
                  }}
                >
                  <strong>Reason:</strong> {declineReason}
                </div>
              )}
            </>
          )}

          <div className="offer-details">{formatOfferLetterContent(offerLetterContent)}</div>

          {/* Candidate Signature Section - Only show if offer is accepted (status 7) */}
          {status === 7 && (candidateSignatureMode || candidateSignatureName || candidateSignatureUrl) && (
            <div
              style={{
                marginTop: "30px",
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: "20px",
              }}
            >
              <div
                style={{
                  textAlign: "right",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Candidate Signature:
                </div>
                {candidateSignatureMode !== "typed" && candidateSignatureUrl ? (
                  <img
                    src={candidateSignatureUrl}
                    alt="Candidate Signature"
                    style={{
                      maxWidth: "250px",
                      maxHeight: "80px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      padding: "8px",
                      backgroundColor: "#fff",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      fontFamily: "'Brush Script MT', cursive",
                      fontSize: "24px",
                      color: "#1f2937",
                      fontStyle: "italic",
                      padding: "8px 16px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      minWidth: "200px",
                    }}
                  >
                    {candidateSignatureName}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
